/**
 * Assessment API Client
 * ---------------------
 * Handles communication with the Lambda API for assessment persistence.
 * Manages diagnostics and tests (quizzes, mocks, timed tests).
 */

import { firebaseAuth } from './firebase.js';

const API_BASE = import.meta.env.VITE_API_BASE;

/**
 * Helper for fetch with error handling
 */
async function apiFetch(path, options = {}) {
  const user = firebaseAuth.currentUser;
  const token = user ? await user.getIdToken() : null;
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    },
    ...options
  });

  if (!response.ok) {
    const error = new Error(`API error: ${response.status} ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export const assessmentApi = {
  // ─────────────────────────────────────────────────────────────
  // Diagnostics
  // ─────────────────────────────────────────────────────────────

  /**
   * Load all diagnostics for a child
   * @param {string} childId
   * @returns {Promise<{diagnostics: Array}>}
   */
  async getDiagnostics(childId) {
    return apiFetch(`/diagnostics/${encodeURIComponent(childId)}`);
  },

  /**
   * Save a new diagnostic attempt
   * @param {string} childId
   * @param {Object} report - Diagnostic report with results
   * @param {Object} profile - Optional profile with abilityBand
   * @returns {Promise<{success: boolean, diagnostic: Object}>}
   */
  async saveDiagnostic(childId, report, profile = null) {
    const body = { report };
    if (profile) {
      body.profile = profile;
    }
    return apiFetch(`/diagnostics/${encodeURIComponent(childId)}`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  // ─────────────────────────────────────────────────────────────
  // Tests (quizzes, mocks, timed)
  // ─────────────────────────────────────────────────────────────

  /**
   * Load all tests for a child
   * @param {string} childId
   * @returns {Promise<{tests: Array}>}
   */
  async getTests(childId) {
    return apiFetch(`/tests/${encodeURIComponent(childId)}`);
  },

  /**
   * Save a new test attempt
   * @param {string} childId
   * @param {Object} options - { kind, score, maxScore, meta }
   * @returns {Promise<{success: boolean, test: Object}>}
   */
  async saveTest(childId, { kind, score, maxScore, meta = {} }) {
    return apiFetch(`/tests/${encodeURIComponent(childId)}`, {
      method: 'POST',
      body: JSON.stringify({ kind, score, maxScore, meta })
    });
  },

  // ─────────────────────────────────────────────────────────────
  // Convenience: Load all assessments in parallel
  // ─────────────────────────────────────────────────────────────

  /**
   * Load all assessment data for a child (parallel fetch)
   * @param {string} childId
   * @returns {Promise<{diagnostics: Array, tests: Array}>}
   */
  async loadAll(childId) {
    const [diagnosticsData, testsData] = await Promise.all([
      this.getDiagnostics(childId).catch(err => {
        console.warn('Failed to load diagnostics:', err);
        return { diagnostics: [] };
      }),
      this.getTests(childId).catch(err => {
        console.warn('Failed to load tests:', err);
        return { tests: [] };
      })
    ]);

    return {
      diagnostics: diagnosticsData.diagnostics || [],
      tests: testsData.tests || []
    };
  }
};

export default assessmentApi;
